// In-process domain-event bus (Document 11 §3/§4). Same event vocabulary the
// production event-driven Gamification/Analytics services will consume
// (Document 3 §6) — only the transport differs (function calls vs. a queue).
export type DomainEventName =
  | 'episode.started'
  | 'episode.completed'
  | 'quiz.started'
  | 'quiz.passed'
  | 'quiz.failed'
  | 'badge.awarded'
  | 'certificate.issued'
  // P4: gamification/immersive-learning events (Document 11 §P4). Same
  // in-process bus, same "function calls now, queue later" transport story
  // as the P0-P3 events above.
  | 'scenario.completed'
  | 'story.completed'
  | 'escaperoom.completed'
  | 'streak.milestone'

export interface DomainEvent<T = unknown> {
  name: DomainEventName
  payload: T
  occurredAt: string
}

// Listeners may be async (e.g. GamificationService awarding XP/badges/a
// certificate). emit() awaits every listener before resolving, so a caller
// that does `await domainEvents.emit(...)` is guaranteed the side effects
// are persisted before it navigates or reads dependent state — this is
// what keeps the P1 Quiz -> Results flow race-free (Document 11 §4).
type Listener = (event: DomainEvent) => void | Promise<void>

class DomainEventBus {
  private listeners = new Map<DomainEventName, Set<Listener>>()

  async emit<T>(name: DomainEventName, payload: T): Promise<void> {
    const event: DomainEvent<T> = { name, payload, occurredAt: new Date().toISOString() }
    const direct = Array.from(this.listeners.get(name) ?? [])
    // Every event also reaches wildcard subscribers (used by LocalAnalyticsSink).
    const wildcard = Array.from(this.listeners.get('*' as DomainEventName) ?? [])
    await Promise.all([...direct, ...wildcard].map((l) => l(event)))
  }

  on(name: DomainEventName | '*', listener: Listener): () => void {
    const key = name as DomainEventName
    if (!this.listeners.has(key)) this.listeners.set(key, new Set())
    this.listeners.get(key)!.add(listener)
    return () => this.listeners.get(key)?.delete(listener)
  }
}

export const domainEvents = new DomainEventBus()
